import React, { useState, useEffect } from 'react';
import { Modal, Form, Button } from 'react-bootstrap';
import axios from 'axios';
import { PencilSquare, Trash, Plus, ChevronUp, ChevronDown, ArrowDownUp, Copy } from 'react-bootstrap-icons';
import ReactMarkdown from 'react-markdown';
import { useWindowWidth } from '../hooks/useWindowWidth';
import { useWeekNav } from '../contexts/WeekNavContext';
import { getCurrentWeek, getWeekDates, getWeekNumberForDate, toISO, formatDate } from '../utils/dateUtils';
import { SUBJECT_STYLES, getSubjectStyle } from '../utils/subjectStyles';
import { getClassStyle } from '../utils/classStyles';
import ScheduleEditor from '../components/ScheduleEditor';

const DAYS_LONG = ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag'];

function SubjectChip({ subjectId, subjects }) {
  const s = getSubjectStyle(subjectId, subjects);
  const name = subjects.find(sub => sub.id === subjectId)?.name || 'Allgemein';
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center',
      padding: '3px 10px', borderRadius: 100,
      fontSize: 11.5, fontWeight: 600, letterSpacing: '0.01em',
      background: s.tint, color: s.ink, whiteSpace: 'nowrap',
    }}>
      {name}
    </span>
  );
}

function Empty({ text }) {
  return (
    <div style={{ padding: '48px 0', textAlign: 'center', color: 'var(--ink-3)', fontSize: 14 }}>{text}</div>
  );
}

function TeacherDashboard() {
  const width = useWindowWidth();
  const isMobile = width < 768;

  const [subjects,      setSubjects]      = useState([]);
  const [classes,       setClasses]       = useState([]);
  const { selectedWeek, setSelectedWeek } = useWeekNav();
  const [activeTab,     setActiveTab]     = useState('tagesplan');
  const [lessons,       setLessons]       = useState([]);
  const [selectedDay,   setSelectedDay]   = useState(() => {
    const dow = new Date().getDay();        // 0=Sun … 6=Sat
    return dow >= 1 && dow <= 5 ? dow - 1 : 0;
  });
  const [allLessons,    setAllLessons]    = useState([]);
  const [sortBy,        setSortBy]        = useState('date');
  const [sortDir,       setSortDir]       = useState('asc');

  const [showLessonModal, setShowLessonModal] = useState(false);
  const [editingLesson,   setEditingLesson]   = useState(null);
  const [lessonForm,      setLessonForm]      = useState({ subjectId: 'math', topic: '', materials: '', duration: 1, classId: '' });

  const [showCopyModal,   setShowCopyModal]   = useState(false);
  const [copyTargetWeek,  setCopyTargetWeek]  = useState(1);

  const [templates,      setTemplates]      = useState([]);
  const [saveAsTemplate, setSaveAsTemplate] = useState(false);
  const [templateName,   setTemplateName]   = useState('');

  const [scheduleEntries, setScheduleEntries] = useState([]);
  const [lessonTimes,     setLessonTimes]     = useState([]);
  const [maxLessons,      setMaxLessons]      = useState(9);

  const token   = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };
  const teacher = JSON.parse(localStorage.getItem('user') || 'null');

  const weekDates    = getWeekDates(selectedWeek);
  const selectedDate = toISO(weekDates[selectedDay]);

  useEffect(() => {
    fetchSubjects(); fetchAllLessons(); fetchTemplates(); fetchClasses(); fetchSchedule();
    axios.get('/api/settings').then(r => {
      setLessonTimes(r.data.lessonTimes || []);
      setMaxLessons(r.data.maxLessons || 9);
    }).catch(() => {});
  }, []);
  useEffect(() => { fetchLessons(selectedDate); }, [selectedDate]);

  async function fetchSubjects() {
    try { const r = await axios.get('/api/admin/subjects', { headers }); setSubjects(r.data); } catch (e) { console.error(e); }
  }
  async function fetchClasses() {
    try { const r = await axios.get('/api/admin/classes'); setClasses([...r.data].sort((a, b) => a.name.localeCompare(b.name, 'de'))); } catch (e) { console.error(e); }
  }
  async function fetchLessons(date) {
    try { const r = await axios.get(`/api/lessons?date=${date}`); setLessons(r.data); }
    catch (e) { setLessons([]); }
  }
  async function fetchAllLessons() {
    try { const r = await axios.get('/api/lessons'); setAllLessons(r.data); }
    catch (e) { setAllLessons([]); }
  }
  async function fetchTemplates() {
    try { const r = await axios.get('/api/templates'); setTemplates(r.data); }
    catch (e) { console.error(e); }
  }
  async function fetchSchedule() {
    try {
      const me = JSON.parse(localStorage.getItem('user') || 'null');
      const r = await axios.get('/api/schedule');
      setScheduleEntries(r.data.filter(e => e.teacherId === me?.id));
    } catch (e) { setScheduleEntries([]); }
  }
  // ── Lesson CRUD ───────────────────────────────────────────────────────────
  function openCreateLesson(prefill = null) {
    setEditingLesson(null);
    setLessonForm({
      subjectId: prefill?.subjectId || subjects[0]?.id || 'math',
      topic: '', materials: '',
      duration: prefill?.duration || 1,
      classId: prefill?.classId || '',
    });
    setShowLessonModal(true);
  }
  function openEditLesson(lesson) {
    setEditingLesson(lesson);
    setLessonForm({ subjectId: lesson.subjectId, topic: lesson.topic, materials: lesson.materials, duration: lesson.duration || 1, classId: lesson.classId || '' });
    setShowLessonModal(true);
  }
  function closeLessonModal() {
    setShowLessonModal(false);
    setEditingLesson(null);
    setSaveAsTemplate(false);
    setTemplateName('');
  }
  async function handleSaveLesson() {
    if (!lessonForm.topic.trim()) return;
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };
    try {
      if (editingLesson) await axios.put(`/api/lessons/${editingLesson.id}`, lessonForm, { headers });
      else               await axios.post('/api/lessons', { ...lessonForm, date: selectedDate }, { headers });
      if (saveAsTemplate && templateName.trim()) {
        await axios.post('/api/templates', {
          name: templateName.trim(),
          subjectId: lessonForm.subjectId,
          topic: lessonForm.topic,
          materials: lessonForm.materials || '',
        });
        setSaveAsTemplate(false); setTemplateName('');
        fetchTemplates();
      }
      closeLessonModal(); fetchLessons(selectedDate); fetchAllLessons();
    } catch (e) { console.error(e); }
  }
  async function handleDeleteLesson(lessonId) {
    const token = localStorage.getItem('token');
    try {
      await axios.delete(`/api/lessons/${lessonId}`, { headers: { Authorization: `Bearer ${token}` } });
      fetchLessons(selectedDate); fetchAllLessons();
    } catch (e) { console.error(e); }
  }

  // ── Week Copy ─────────────────────────────────────────────────────────────
  async function handleCopyWeek() {
    if (copyTargetWeek === selectedWeek) return;
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };
    const srcDates = getWeekDates(selectedWeek).map(toISO);
    const tgtDates = getWeekDates(copyTargetWeek).map(toISO);
    const weekLessonsAll = allLessons.filter(l => srcDates.includes(l.date));
    for (const l of weekLessonsAll) {
      const di = srcDates.indexOf(l.date);
      if (di === -1) continue;
      await axios.post('/api/lessons', {
        subjectId: l.subjectId, topic: l.topic, materials: l.materials,
        duration: l.duration || 1, date: tgtDates[di],
      }, { headers });
    }
    setShowCopyModal(false);
    fetchAllLessons();
  }

  // ── Sort ──────────────────────────────────────────────────────────────────
  function toggleSort(field) {
    if (sortBy === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortBy(field); setSortDir('asc'); }
  }
  const sortedLessons = [...allLessons].sort((a, b) => {
    let vA, vB;
    if (sortBy === 'topic') { vA = a.topic.toLowerCase(); vB = b.topic.toLowerCase(); }
    else if (sortBy === 'week') { vA = getWeekNumberForDate(a.date); vB = getWeekNumberForDate(b.date); }
    else { vA = a.date; vB = b.date; }
    if (vA < vB) return sortDir === 'asc' ? -1 : 1;
    if (vA > vB) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  // Empty-week ghosts from own recurring schedule (whole-empty-week granularity)
  const weekISOs  = weekDates.map(toISO);
  const weekEmpty = !allLessons.some(l => weekISOs.includes(l.date));
  const dayGhosts = weekEmpty ? scheduleEntries.filter(e => e.dayIndex === selectedDay) : [];

  return (
    <div style={{ background: 'var(--bg)', minHeight: 'calc(100vh - 57px)', padding: isMobile ? '20px 16px 60px' : '32px 40px 60px' }}>

      {/* ── Header ───────────────────────────────────────────────────────── */}
      <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 28, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 className="display" style={{ fontSize: 36, margin: 0 }}>Wochenübersicht</h1>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn" onClick={() => { setCopyTargetWeek(selectedWeek < 52 ? selectedWeek + 1 : 1); setShowCopyModal(true); }} title="Woche kopieren" style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '7px 12px', border: '1px solid var(--line)', borderRadius: 'var(--r-s)', background: 'transparent', color: 'var(--ink-2)', cursor: 'pointer', fontSize: 13 }}>
            <Copy size={14} /> KW kopieren
          </button>
          {activeTab === 'tagesplan' && (
            <button className="btn btn-grad" onClick={() => openCreateLesson()} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <Plus size={16} /> Neue Lektion
            </button>
          )}
        </div>
      </header>

      {/* ── Tab row ──────────────────────────────────────────────────────── */}
      <div className="tab-row" style={{ marginBottom: 28 }}>
        <button className={activeTab === 'tagesplan'   ? 'active' : ''} onClick={() => setActiveTab('tagesplan')}>Tagesplan</button>
        <button className={activeTab === 'uebersicht'  ? 'active' : ''} onClick={() => setActiveTab('uebersicht')}>Übersicht</button>
        <button className={activeTab === 'stundenplan' ? 'active' : ''} onClick={() => setActiveTab('stundenplan')}>Stundenplan</button>
        <button className={activeTab === 'faecher'     ? 'active' : ''} onClick={() => setActiveTab('faecher')}>Fächer</button>
      </div>

      {/* ── Tagesplan ────────────────────────────────────────────────────── */}
      {activeTab === 'tagesplan' && (
        <div>
          {/* ── Day pills ────────────────────────────────────────────────── */}
          <div style={{ display: 'flex', gap: isMobile ? 6 : 12, marginBottom: 24 }}>
            {DAYS_LONG.map((dayName, i) => {
              const active = i === selectedDay;
              const count  = weekEmpty
                ? scheduleEntries.filter(e => e.dayIndex === i).length
                : allLessons.filter(l => l.date === toISO(weekDates[i])).length;
              return (
                <button
                  key={dayName}
                  aria-pressed={active}
                  onClick={() => setSelectedDay(i)}
                  style={{
                    flex: 1, padding: isMobile ? '10px 4px' : '14px 18px', textAlign: 'left',
                    background: active ? 'var(--ink-1)' : 'var(--paper)',
                    border: '1px solid var(--line)',
                    borderRadius: 'var(--r-l)',
                    color: active ? 'var(--bg)' : 'var(--ink-1)',
                    cursor: 'pointer',
                    transition: 'background 0.15s, color 0.15s, border-color 0.15s, box-shadow 0.15s',
                    boxShadow: active ? 'var(--sh-2)' : 'none',
                  }}
                >
                  <div className="mono" style={{
                    fontSize: '0.81rem', letterSpacing: '0.08em', textTransform: 'uppercase',
                    color: active ? undefined : 'var(--ink-3)',
                    opacity: active ? 0.65 : 1,
                  }}>
                    {formatDate(weekDates[i])}
                  </div>
                  <div className="display" style={{ fontSize: isMobile ? '1.06rem' : '1.375rem', marginTop: 4, color: 'inherit' }}>
                    {isMobile ? dayName.slice(0, 2) : dayName}
                  </div>
                  <div style={{ fontSize: '0.875rem', marginTop: 6, color: active ? undefined : 'var(--ink-3)', opacity: active ? 0.65 : 1 }}>
                    {count} {isMobile ? 'L' : `Lektion${count !== 1 ? 'en' : ''}`}
                  </div>
                </button>
              );
            })}
          </div>
          {lessons.length === 0 && dayGhosts.length > 0 && (
            <>
              <p style={{ fontSize: 13, color: 'var(--ink-3)', margin: '0 0 14px' }}>
                Stundenplan-Vorschau — klicke einen Eintrag an, um daraus eine Lektion zu erstellen.
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {[...dayGhosts].sort((a, b) => a.slotIndex - b.slotIndex).map(g => {
                  const s = getSubjectStyle(g.subjectId, subjects);
                  const cst = getClassStyle(g.classId, classes);
                  const name = subjects.find(sub => sub.id === g.subjectId)?.name || 'Allgemein';
                  return (
                    <div
                      key={g.id}
                      onClick={() => openCreateLesson({ subjectId: g.subjectId, classId: g.classId, duration: g.duration || 1 })}
                      title="Lektion aus Stundenplan erstellen"
                      className="card-flat"
                      style={{
                        padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 10,
                        borderLeft: `3px solid color-mix(in srgb, ${s.ink} 55%, transparent)`,
                        borderStyle: 'dashed', cursor: 'pointer', opacity: 0.8,
                      }}
                    >
                      <span style={{ fontWeight: 600, fontSize: 14, color: s.ink }}>{name}</span>
                      {cst && (
                        <span className="mono" style={{
                          fontSize: 10, fontWeight: 700, color: cst.ink, background: cst.tint,
                          border: `1px solid color-mix(in srgb, ${cst.ink} 35%, transparent)`,
                          borderRadius: 100, padding: '2px 8px',
                        }}>{cst.name}</span>
                      )}
                      {g.duration >= 2 && <span className="mono" style={{ fontSize: 10, color: 'var(--ink-3)' }}>Doppelstunde</span>}
                      <Plus size={14} style={{ marginLeft: 'auto', color: 'var(--ink-4)' }} />
                    </div>
                  );
                })}
              </div>
            </>
          )}
          {lessons.length === 0 && dayGhosts.length === 0
            ? <Empty text={`Keine Lektionen für ${new Date(selectedDate + 'T12:00:00').toLocaleDateString('de-CH', { weekday: 'long', day: '2-digit', month: '2-digit' })}.`} />
            : lessons.length === 0 ? null : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {lessons.map(lesson => {
                  const s = getSubjectStyle(lesson.subjectId, subjects);
                  return (
                    <div key={lesson.id} className="card-flat" style={{
                      padding: '16px 18px', display: 'flex', gap: 14, alignItems: 'flex-start',
                      borderLeft: `3px solid ${s.ink}`,
                    }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                          <SubjectChip subjectId={lesson.subjectId} subjects={subjects} />
                          {(() => {
                            const cst = getClassStyle(lesson.classId, classes);
                            return cst && (
                              <span className="mono" style={{
                                display: 'inline-flex', alignItems: 'center',
                                fontSize: 10, fontWeight: 700, color: cst.ink, background: cst.tint,
                                border: `1px solid color-mix(in srgb, ${cst.ink} 35%, transparent)`,
                                borderRadius: 100, padding: '2px 8px', whiteSpace: 'nowrap',
                              }}>
                                {cst.name}
                              </span>
                            );
                          })()}
                        </div>
                        <div style={{ fontWeight: 600, fontSize: 15, color: 'var(--ink-1)', marginBottom: 4 }}>{lesson.topic}</div>
                        {lesson.materials && (
                          <div className="markdown-body" style={{ fontSize: 13, color: 'var(--ink-3)' }}>
                            <ReactMarkdown>{lesson.materials}</ReactMarkdown>
                          </div>
                        )}
                      </div>
                      <div style={{ display: 'flex', gap: 4, flexShrink: 0, marginTop: 2 }}>
                        <button className="btn-icon" onClick={() => openEditLesson(lesson)} title="Bearbeiten"><PencilSquare size={14} /></button>
                        <button className="btn-icon" onClick={() => handleDeleteLesson(lesson.id)} title="Löschen"><Trash size={14} /></button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )
          }
        </div>
      )}

      {/* ── Übersicht ────────────────────────────────────────────────────── */}
      {activeTab === 'uebersicht' && (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
            <span className="mono" style={{ fontSize: 11, color: 'var(--ink-3)', letterSpacing: '0.06em' }}>Sortieren:</span>
            {[{ key: 'date', label: 'Datum' }, { key: 'week', label: 'Woche' }, { key: 'topic', label: 'Thema' }].map(({ key, label }) => (
              <button
                key={key}
                onClick={() => toggleSort(key)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 4,
                  padding: '5px 10px', borderRadius: 'var(--r-s)', cursor: 'pointer',
                  border: `1px solid ${sortBy === key ? 'var(--accent)' : 'var(--line)'}`,
                  background: sortBy === key ? 'var(--accent-soft)' : 'transparent',
                  color: sortBy === key ? 'var(--accent)' : 'var(--ink-2)',
                  fontSize: 12, fontWeight: 500,
                  transition: 'all 0.15s',
                }}
              >
                {label}
                {sortBy === key
                  ? (sortDir === 'asc' ? <ChevronUp size={11} /> : <ChevronDown size={11} />)
                  : <ArrowDownUp size={11} />}
              </button>
            ))}
          </div>

          {sortedLessons.length === 0
            ? <Empty text="Noch keine Lektionen." />
            : (
              <div className="card-flat scroll-x" style={{ overflow: 'auto' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '110px 1fr 120px', padding: '10px 18px', background: 'var(--surface)', borderBottom: '1px solid var(--line)', gap: 16, minWidth: 400 }}>
                  {['Fach', 'Thema', 'Datum / KW'].map((h, i) => (
                    <div key={i} className="mono" style={{ fontSize: 10, color: 'var(--ink-3)', letterSpacing: '0.08em', textTransform: 'uppercase' }}>{h}</div>
                  ))}
                </div>
                {sortedLessons.map((lesson, i) => (
                  <div key={lesson.id} style={{
                    display: 'grid', gridTemplateColumns: '110px 1fr 120px',
                    padding: '12px 18px', alignItems: 'center', gap: 16,
                    borderBottom: i < sortedLessons.length - 1 ? '1px solid var(--line-soft)' : 'none',
                    minWidth: 400,
                  }}>
                    <div style={{ overflow: 'hidden', maxWidth: '100%' }}>
                      <SubjectChip subjectId={lesson.subjectId} subjects={subjects} />
                    </div>
                    <div style={{ fontSize: 13.5, fontWeight: 500, color: 'var(--ink-1)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {lesson.topic}
                    </div>
                    <div>
                      <div className="mono" style={{ fontSize: 12, color: 'var(--ink-2)' }}>
                        {new Date(lesson.date + 'T12:00:00').toLocaleDateString('de-CH', { weekday: 'short', day: '2-digit', month: '2-digit' })}
                      </div>
                      <div className="mono" style={{ fontSize: 10, color: 'var(--ink-3)', marginTop: 2 }}>
                        KW {getWeekNumberForDate(lesson.date)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )
          }
        </div>
      )}

      {/* ── Stundenplan ──────────────────────────────────────────────────── */}
      {activeTab === 'stundenplan' && (
        <ScheduleEditor
          subjects={subjects} classes={classes} maxLessons={maxLessons} lessonTimes={lessonTimes}
          authHdr={headers} teacherId={teacher?.id} isMobile={isMobile}
        />
      )}

      {/* ── Fächer ───────────────────────────────────────────────────────── */}
      {activeTab === 'faecher' && (
        <div>
          {subjects.length === 0
            ? <Empty text="Keine Fächer vorhanden." />
            : (
              <div className="card-flat">
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 80px', padding: '10px 18px', background: 'var(--surface)', borderBottom: '1px solid var(--line)', gap: 16 }}>
                  {['Fach', 'Farbe'].map((h, i) => (
                    <div key={i} className="mono" style={{ fontSize: 10, color: 'var(--ink-3)', letterSpacing: '0.08em', textTransform: 'uppercase' }}>{h}</div>
                  ))}
                </div>
                {subjects.map((subject, i) => (
                  <div key={subject.id} style={{
                    display: 'grid', gridTemplateColumns: '1fr 80px',
                    padding: '12px 18px', alignItems: 'center', gap: 16,
                    borderBottom: i < subjects.length - 1 ? '1px solid var(--line-soft)' : 'none',
                  }}>
                    <div style={{ fontSize: 13.5, fontWeight: 500, color: 'var(--ink-1)' }}>{subject.name}</div>
                    <input
                      type="color"
                      value={subject.color || '#7287fd'}
                      onChange={e => {
                        const color = e.target.value;
                        axios.put(`/api/admin/subjects/${subject.id}`, { color }, { headers }).catch(err => console.error(err));
                        setSubjects(prev => prev.map(s => s.id === subject.id ? { ...s, color } : s));
                      }}
                      style={{ width: 40, height: 28, padding: 0, border: '1px solid var(--line)', borderRadius: 'var(--r-s)', cursor: 'pointer', background: 'none' }}
                    />
                  </div>
                ))}
              </div>
            )
          }
        </div>
      )}

      {/* ── Lesson Modal ─────────────────────────────────────────────────── */}
      <Modal show={showLessonModal} onHide={closeLessonModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>{editingLesson ? 'Lektion bearbeiten' : 'Neue Lektion'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            {templates.length > 0 && (
              <Form.Group className="mb-3">
                <Form.Label>Aus Vorlage laden</Form.Label>
                <Form.Select defaultValue="_none" onChange={e => {
                  const tpl = templates.find(t => t.id === e.target.value);
                  if (tpl) setLessonForm(f => ({ ...f, subjectId: tpl.subjectId, topic: tpl.topic, materials: tpl.materials || '' }));
                  e.target.value = '_none';
                }}>
                  <option value="_none">— Vorlage wählen —</option>
                  {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </Form.Select>
              </Form.Group>
            )}
            <Form.Group className="mb-3">
              <Form.Label>Fach</Form.Label>
              <Form.Select value={lessonForm.subjectId} onChange={e => setLessonForm({ ...lessonForm, subjectId: e.target.value })}>
                {subjects.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Klasse</Form.Label>
              <Form.Select value={lessonForm.classId} onChange={e => setLessonForm({ ...lessonForm, classId: e.target.value })}>
                <option value="">Alle Klassen</option>
                {classes.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Thema</Form.Label>
              <Form.Control type="text" value={lessonForm.topic}
                onChange={e => setLessonForm({ ...lessonForm, topic: e.target.value })}
                placeholder="z.B. Bruchrechnen – Addition" required />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Materialien / Hausaufgaben</Form.Label>
              <Form.Control as="textarea" rows={3} value={lessonForm.materials}
                onChange={e => setLessonForm({ ...lessonForm, materials: e.target.value })}
                placeholder="z.B. Lehrbuch S. 78–80, Aufgabe 5" />
              <Form.Text className="text-muted">Markdown wird unterstützt</Form.Text>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Dauer</Form.Label>
              <Form.Select value={lessonForm.duration} onChange={e => setLessonForm({ ...lessonForm, duration: parseInt(e.target.value) })}>
                <option value={1}>1 Lektion</option>
                <option value={2}>2 Lektionen (Doppelstunde)</option>
              </Form.Select>
            </Form.Group>
            <Form.Group className="mt-3">
              <Form.Check
                type="checkbox"
                label="Als Vorlage speichern"
                checked={saveAsTemplate}
                onChange={e => { setSaveAsTemplate(e.target.checked); if (!templateName) setTemplateName(lessonForm.topic); }}
              />
              {saveAsTemplate && (
                <Form.Control className="mt-2" type="text" value={templateName}
                  onChange={e => setTemplateName(e.target.value)}
                  placeholder="Vorlagenname" />
              )}
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeLessonModal}>Abbrechen</Button>
          <Button variant="primary" onClick={handleSaveLesson}>{editingLesson ? 'Speichern' : 'Erstellen'}</Button>
        </Modal.Footer>
      </Modal>

      {/* ── Copy Week Modal ──────────────────────────────────────────────── */}
      <Modal show={showCopyModal} onHide={() => setShowCopyModal(false)} centered size="sm">
        <Modal.Header closeButton><Modal.Title>Woche kopieren</Modal.Title></Modal.Header>
        <Modal.Body>
          <p style={{ fontSize: 14, color: 'var(--ink-2)', marginBottom: 16 }}>
            Alle Lektionen von KW {selectedWeek} in eine andere Woche kopieren.
          </p>
          <Form.Group>
            <Form.Label>Zielwoche (KW)</Form.Label>
            <Form.Control type="number" min={1} max={52} value={copyTargetWeek}
              onChange={e => setCopyTargetWeek(parseInt(e.target.value) || 1)} />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCopyModal(false)}>Abbrechen</Button>
          <Button variant="primary" onClick={handleCopyWeek}>Kopieren</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default TeacherDashboard;
